import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { ssoSignOut } from "../lib/ssoSession";
import { useAdminTheme } from "../lib/useAdminTheme";
import { Moon, Sun, Monitor, HelpCircle } from "lucide-react";
import SessionTimeoutGuard from "../components/SessionTimeoutGuard";
import ToolkitsPanel from "../components/admin/ToolkitsPanel";
import AssessmentsPanel from "../components/admin/AssessmentsPanel";
import SiteContentPanel from "../components/admin/SiteContentPanel";
import CaseStudiesPanel from "../components/admin/CaseStudiesPanel";
import AssessmentLeadsPanel from "../components/admin/AssessmentLeadsPanel";
import PortfolioLeadsPanel from "../components/admin/PortfolioLeadsPanel";
import LeadBackupPanel from "../components/admin/LeadBackupPanel";
import StakeholderReportPanel from "../components/admin/StakeholderReportPanel";
import TrainingPanel from "../components/admin/TrainingPanel";
import SeoPanel from "../components/admin/SeoPanel";
import OgCopyPanel from "../components/admin/OgCopyPanel";
import UsersPanel from "../components/admin/UsersPanel";
import DocsAccessPanel from "../components/admin/DocsAccessPanel";
import RateCardPanel from "../components/admin/RateCardPanel";
import DealRateCardsPanel from "../components/admin/DealRateCardsPanel";
import TrialsPanel from "../components/admin/TrialsPanel";
import CampaignsPanel from "../components/admin/CampaignsPanel";
import LeadsPanel from "../components/admin/LeadsPanel";
import OpsPanel from "../components/admin/OpsPanel";
import HeatmapPanel from "../components/admin/HeatmapPanel";
import DraftNudgePanel from "../components/admin/DraftNudgePanel";
import HelpPanel from "../components/admin/HelpPanel";
import UserManualPanel from "../components/admin/UserManualPanel";
import DemoAccessPanel from "../components/admin/DemoAccessPanel";
import EmailSettingsPanel from "../components/admin/EmailSettingsPanel";
import SearchAnalyticsPanel from "../components/admin/SearchAnalyticsPanel";
import BrandAssetsPanel from "../components/admin/BrandAssetsPanel";
import LegalIpPanel from "../components/admin/LegalIpPanel";
import GovernancePanel from "../components/admin/GovernancePanel";
import ContentGovernancePanel from "../components/admin/ContentGovernancePanel";
import InterRaterPanel from "../components/admin/InterRaterPanel";
import ErpBlueprintsPanel from "../components/admin/ErpBlueprintsPanel";
import Enterprise360Panel from "../components/admin/Enterprise360Panel";
import SelfAssessmentPanel from "../components/admin/SelfAssessmentPanel";
import InstanceBrandingPanel from "../components/admin/InstanceBrandingPanel";
import EnvironmentHealthPanel from "../components/admin/EnvironmentHealthPanel";
import DeliveryTomPanel from "../components/admin/DeliveryTomPanel";
import OrganisationsPanel from "../components/admin/OrganisationsPanel";
import AdminTour from "../components/admin/AdminTour";
import RoleGate from "../auth/RoleGate";
import ImpersonationBanner from "../components/admin/ImpersonationBanner";
import AuditLogPanel from "../components/admin/AuditLogPanel";
import CrossAppPanel from "../components/admin/CrossAppPanel";
import ResourcesPanel from "../components/admin/ResourcesPanel";
import AdminNav from "../components/admin/AdminNav";
import RolePreviewSwitcher from "../components/admin/RolePreviewSwitcher";
import { ContactsPanel, BrochuresPanel, ResourceLeadsPanel, NewsletterPanel } from "../components/admin/SimpleLeadPanels";
import PreviewLeadsPanel from "../components/admin/PreviewLeadsPanel";
import CRM360Panel from "../components/admin/CRM360Panel";
import CalibrationMapPanel from "../components/admin/CalibrationMapPanel";
import ProofDashboardPanel from "../components/admin/ProofDashboardPanel";
import PlaybookPanel from "../components/admin/PlaybookPanel";

const Tabs = ["Contacts", "Brochure requests", "Resource leads", "Newsletter", "Resources", "Toolkits"];

// Friendly labels for the header role badge (drives the "your access" hint).
const ROLE_LABELS = {
  founder: "Founder · full access",
  sales_manager: "Sales Manager",
  sales_person: "Sales",
  trial_user: "Trial",
  campaign_user: "Campaign",
  lite_public: "Lite",
};

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(location.state?.user || null);
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState(Tabs[0]);

  // Cross-panel tab-switch event (used by other admin panels via window.dispatchEvent)
  useEffect(() => {
    const onSwitch = (e) => { if (e?.detail) setTab(e.detail); };
    window.addEventListener("admin-switch-tab", onSwitch);
    // OAuth return (?tab=integrations) opens the Relationship Hub → Integrations subtab.
    const p = new URLSearchParams(window.location.search);
    if (p.get("tab") === "integrations") setTab("Relationship Hub");
    return () => window.removeEventListener("admin-switch-tab", onSwitch);
  }, []);

  // Independent admin theme (light/dark) — separate from the public-site theme.
  const { mode: themeMode, setMode: setThemeMode } = useAdminTheme();

  // "New since your last visit" dot on the User manual tab
  const [manualUnread, setManualUnread] = useState(false);
  const [manualJump, setManualJump] = useState(null);
  const [contentSignoff, setContentSignoff] = useState(0);
  useEffect(() => {
    api.get("/admin/user-manual").then(({ data }) => {
      if (data?.version && data.version !== data.last_seen_version) setManualUnread(true);
    }).catch(() => {});
  }, []);
  // Founder nudge: count content sources awaiting Ekselens sign-off.
  useEffect(() => {
    if (me?.platform_role !== "founder") return;
    api.get("/admin/enterprise360/content-governance")
      .then(({ data }) => setContentSignoff(data?.pending_signoff || 0))
      .catch(() => {});
  }, [me?.platform_role, tab]);
  const openManual = (section) => {
    setManualJump(typeof section === "string" ? section : null);
    setTab("User manual");
    if (manualUnread) {
      setManualUnread(false);
      api.post("/admin/user-manual/seen").catch(() => {});
    }
  };
  // Map the current admin tab → the User-Manual section heading, so the header
  // "?" opens the manual scrolled to the relevant "how to use it" section.
  const sectionForTab = (t) => {
    const s = (t || "").toLowerCase();
    const rules = [
      [["enterprise360", "blueprint", "value stream", "standards", "regulat", "benchmark", "governance", "delivery", "assurance", "raid", "egmm", "maturity"], "5. ENTERPRISE360"],
      [["crm", "lead", "prospect", "proposal", "engagement", "relationship", "deal", "pipeline"], "13. CRM360"],
      [["assessment", "run", "evidence", "csi"], "2. The Assessment workflow"],
      [["deliverable", "export", "report", "board", "pack"], "4. Deliverable Studio"],
      [["rate card", "pricing", "benefit", "value"], "14. Pricing"],
      [["user", "trial", "role", "invit"], "15. Users, roles"],
      [["integration", "quickbook", "microsoft", "m365", "outlook"], "11. Integrations"],
      [["site content", "legal", "testimonial"], "19. Site content"],
    ];
    for (const [keys, section] of rules) {
      if (keys.some((k) => s.includes(k))) return section;
    }
    return null;
  };
  const [contacts, setContacts] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [resourceLeads, setResourceLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [resources, setResources] = useState([]);

  // Auth check
  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        navigate("/admin/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [user, navigate]);

  // Fetch canonical /api/me (permissions + ui_flags) once we have an
  // authenticated admin. Falls back gracefully if the endpoint isn't
  // reachable — Admin still works on the existing Emergent-only path.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/me");
        setMe(data);
      } catch (e) {
        // Non-fatal — Admin functions without the policy bundle.
      }
    })();
  }, [user]);

  // Founder-only "view as role" preview — re-scopes the visible menus via
  // /api/policies/{role}; server-side permissions are untouched.
  const [previewRole, setPreviewRole] = useState(null);
  const [previewFlags, setPreviewFlags] = useState(null);
  useEffect(() => {
    if (!previewRole) { setPreviewFlags(null); return; }
    api.get(`/policies/${previewRole}`)
      .then(({ data }) => setPreviewFlags(data.ui_flags || {}))
      .catch(() => setPreviewFlags({}));
  }, [previewRole]);
  const isFounder = me?.platform_role === "founder";
  const effectiveMe = (previewRole && previewFlags)
    ? { ...me, platform_role: previewRole, ui_flags: previewFlags }
    : me;

  const reload = useCallback(async () => {
    try {
      const [c, b, rl, ns, rs] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/brochure-requests"),
        api.get("/admin/resource-leads"),
        api.get("/admin/newsletter"),
        api.get("/resources"),
      ]);
      setContacts(c.data.items || []);
      setBrochures(b.data.items || []);
      setResourceLeads(rl.data.items || []);
      setSubscribers(ns.data.items || []);
      setResources(rs.data || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (user) reload();
  }, [user, reload]);

  const logout = () => {
    // Unified sign-out: the OIDC logout endpoint clears the admin session
    // (session_token) AND the shared Auth0 SSO session in one step — no more
    // having to sign out twice.
    ssoSignOut();
  };

  if (checking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="admin-loading">
        Checking session…
      </div>
    );
  }
  if (!user) return null;

  return (
    <section className="bg-white ek-admin" data-testid="admin-dashboard">
      <SessionTimeoutGuard redirectTo="/admin/login" />
      <AdminTour />
      <ImpersonationBanner me={me} />
      {/* Header strip */}
      <div className="bg-black text-white">
        <div className="ek-container py-8 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="ek-eyebrow text-[#A179FA]">Admin</div>
            <h1 className="ek-heading text-2xl md:text-3xl mt-2">
              Welcome, {user.name?.split(" ")[0] || "Admin"}
            </h1>
            <p className="text-zinc-400 text-[13px] mt-1">{user.email}</p>
            {me?.platform_role && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2.5" data-testid="admin-role-badge">
                <span
                  title={`Signed in as ${ROLE_LABELS[me.platform_role] || me.platform_role}. Your menus reflect this access level.`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.14em] uppercase text-[#A179FA] border border-[#7737F6]/50 px-2.5 py-1"
                  data-testid="admin-role-badge-label"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#7737F6]" />
                  {ROLE_LABELS[me.platform_role] || me.platform_role}
                </span>
                <span className="text-zinc-500 text-[11px]">Your menus reflect your access level</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openManual(sectionForTab(tab))}
              aria-label="Open the user manual for this screen"
              title="Help for this screen"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-white/80 hover:text-white border border-white/30 hover:border-white px-4 py-3 transition-colors"
              data-testid="admin-help-button"
            >
              <HelpCircle size={15} />
              Help
            </button>
            <div
              role="group"
              aria-label="Admin colour theme"
              data-testid="admin-theme-selector"
              className="inline-flex items-stretch border border-white/30 divide-x divide-white/20"
            >
              {[
                { m: "auto", Icon: Monitor, label: "Auto" },
                { m: "light", Icon: Sun, label: "Light" },
                { m: "dark", Icon: Moon, label: "Dark" },
              ].map(({ m, Icon, label }) => {
                const active = themeMode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setThemeMode(m)}
                    aria-pressed={active}
                    title={m === "auto" ? "Match system theme" : `${label} mode`}
                    data-testid={`admin-theme-mode-${m}`}
                    className={`inline-flex items-center gap-1.5 text-[12px] tracking-[0.16em] uppercase px-3 py-3 transition-colors ${
                      active ? "bg-white text-[#1a1024]" : "text-white/70 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={logout}
              className="text-[12px] tracking-[0.16em] uppercase text-white/80 hover:text-white border border-white/30 hover:border-white px-5 py-3 transition-colors"
              data-testid="admin-logout-btn"
            >
              Sign out
            </button>
          </div>
        </div>
        {isFounder && (
          <div className="ek-container pb-6 -mt-2">
            <RolePreviewSwitcher value={previewRole} onChange={setPreviewRole} />
          </div>
        )}
      </div>

      {previewRole && (
        <div className="bg-[#7737F6] text-white" data-testid="role-preview-banner">
          <div className="ek-container py-2.5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[12px] tracking-[0.04em] flex flex-wrap items-center gap-x-3 gap-y-1">
              Previewing as <strong>{ROLE_LABELS[previewRole] || previewRole}</strong>
              {previewFlags && (
                <>
                  <span className="inline-flex items-center gap-1.5 text-[11px] bg-white/15 px-2 py-0.5 rounded-full" data-testid="preview-chip-exports">
                    Exports: {previewFlags.watermark_exports ? "watermarked" : "clean"}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] bg-white/15 px-2 py-0.5 rounded-full" data-testid="preview-chip-pricing">
                    Pricing: {previewFlags.pricing_visible ? "visible" : "hidden"}
                  </span>
                </>
              )}
              <span className="text-white/75">· your real access is unchanged</span>
            </span>
            <button
              type="button"
              onClick={() => setPreviewRole(null)}
              className="text-[11px] tracking-[0.16em] uppercase border border-white/50 hover:bg-white hover:text-[#7737F6] px-4 py-1.5 transition-colors"
              data-testid="role-preview-exit"
            >
              Exit preview
            </button>
          </div>
        </div>
      )}

      {/* Grouped ERIO-style dropdown navigation (role-aware) */}
      <AdminNav
        me={effectiveMe}
        tab={tab}
        setTab={setTab}
        counts={{
          contacts: contacts.length,
          brochures: brochures.length,
          resourceLeads: resourceLeads.length,
          subscribers: subscribers.length,
          resources: resources.length,
          contentSignoff: contentSignoff,
        }}
        manualUnread={manualUnread}
        openManual={openManual}
      />

      <div className="ek-container py-10">
        {tab === "Relationship Hub" && <CRM360Panel me={effectiveMe} />}
        {tab === "Contacts" && <ContactsPanel contacts={contacts} />}
        {tab === "Brochure requests" && <BrochuresPanel brochures={brochures} />}
        {tab === "Resource leads" && <ResourceLeadsPanel resourceLeads={resourceLeads} />}
        {tab === "Newsletter" && <NewsletterPanel subscribers={subscribers} />}
        {tab === "Resources" && <ResourcesPanel resources={resources} onReload={reload} />}

        {tab === "Toolkits" && <ToolkitsPanel />}
        {tab === "Assessments" && <AssessmentsPanel />}
        {tab === "Enterprise360" && <Enterprise360Panel />}
        {tab === "Calibration map" && <CalibrationMapPanel />}
        {tab === "Proof dashboard" && <ProofDashboardPanel />}
        {tab === "Playbook & cert" && <PlaybookPanel />}
        {tab === "Self-assessment" && <SelfAssessmentPanel />}
        {tab === "Instance" && <InstanceBrandingPanel />}
        {tab === "Environment health" && <EnvironmentHealthPanel />}
        {tab === "Delivery TOM" && <DeliveryTomPanel />}
        {tab === "Organisations" && <OrganisationsPanel />}
        {tab === "Site content" && <SiteContentPanel />}
        {tab === "Case studies" && <CaseStudiesPanel />}
        {tab === "Assessment leads" && <AssessmentLeadsPanel />}
        {tab === "Preview leads" && <PreviewLeadsPanel />}
        {tab === "Portfolio leads" && <PortfolioLeadsPanel />}
        {tab === "Lead backup" && <LeadBackupPanel />}
        {tab === "Stakeholder report" && <StakeholderReportPanel />}
        {tab === "Training" && <TrainingPanel />}
        {tab === "Rate cards" && (
          <RoleGate me={me} uiFlag="show_rate_card_master" fallback={null}>
            <RateCardPanel />
          </RoleGate>
        )}
        {tab === "Deal rate cards" && (
          <RoleGate me={me} uiFlag="show_rate_card_deal" fallback={null}>
            <DealRateCardsPanel />
          </RoleGate>
        )}
        {tab === "Trials" && (
          <RoleGate me={me} uiFlag="show_trials_review" fallback={null}>
            <TrialsPanel />
          </RoleGate>
        )}
        {tab === "Demo access" && (
          <RoleGate me={me} uiFlag="show_trials_review" fallback={null}>
            <DemoAccessPanel />
          </RoleGate>
        )}
        {tab === "Campaigns" && (
          <RoleGate me={me} uiFlag="show_campaigns" fallback={null}>
            <CampaignsPanel />
          </RoleGate>
        )}
        {tab === "Leads" && <LeadsPanel onTabSwitch={setTab} />}
        {tab === "Ops" && <OpsPanel />}
        {tab === "Heatmap" && <HeatmapPanel />}
        {tab === "Draft nudges" && <DraftNudgePanel />}
        {tab === "SEO" && <SeoPanel />}
        {tab === "OG copy" && <OgCopyPanel />}
        {tab === "Email" && <EmailSettingsPanel />}
        {tab === "Searches" && <SearchAnalyticsPanel />}
        {tab === "Cross-app" && <CrossAppPanel />}
        {tab === "Brand assets" && <BrandAssetsPanel />}
        {tab === "Users" && <UsersPanel me={me} />}
        {tab === "Docs access" && me?.platform_role === "founder" && <DocsAccessPanel />}
        {tab === "Audit log" && (
          <RoleGate me={me} uiFlag="show_audit_log" fallback={null}>
            <AuditLogPanel />
          </RoleGate>
        )}
        {tab === "Legal & IP" && (
          <RoleGate me={me} uiFlag="show_legal_ip_tab" fallback={null}>
            <LegalIpPanel />
          </RoleGate>
        )}
        {tab === "Help" && <HelpPanel me={me} />}
        {tab === "User manual" && <UserManualPanel jumpTo={manualJump} />}
        {tab === "Governance" && <GovernancePanel />}
        {tab === "Content governance" && me?.platform_role === "founder" && <ContentGovernancePanel />}
        {tab === "Inter-rater" && <InterRaterPanel />}
        {tab === "ERP blueprints" && <ErpBlueprintsPanel isOwner={me?.platform_role === "founder"} />}
      </div>
    </section>
  );
};

export default Admin;
